import { useState } from "react";
import { cn } from "@/lib/utils";
import { ResortButton, ResortLink } from "../resort-button";
import { Eyebrow, MaskReveal, Reveal } from "../reveal";

const preferences = ["Pull-Through", "Back-In", "No Preference"] as const;
const stayTypes = ["Daily", "Weekly", "Monthly"] as const;

export function RVFitBridge() {
  const [length, setLength] = useState(45);
  const [preference, setPreference] = useState<(typeof preferences)[number]>("No Preference");
  const [stayType, setStayType] = useState<(typeof stayTypes)[number]>("Daily");

  return (
    <section aria-labelledby="map-fit-heading" className="bg-secondary/40 py-14 md:py-18">
      <div className="shell grid gap-8 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>Traveling with a Larger Rig?</Eyebrow>
          </Reveal>
          <h2 id="map-fit-heading" className="display-lg mt-6">
            <MaskReveal>Let Us Help</MaskReveal>
            <MaskReveal delay={0.08}>Find Your Space</MaskReveal>
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">
              Auburn RV Resort has sites ranging from approximately 36 feet to more than 120 feet.
              Tell us about your RV and our team can help identify available options that may work
              for your setup.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.08} className="lg:col-span-7">
          <form
            className="border border-border bg-card p-7 md:p-10"
            onSubmit={(e) => {
              e.preventDefault();
              const params = new URLSearchParams({
                length: String(length),
                preference,
                stay: stayType,
              });
              window.location.assign(`/reservations/?${params.toString()}#availability-request`);
            }}
          >
            <div>
              <label htmlFor="map-rv-length" className="eyebrow flex justify-between text-muted-foreground">
                <span>RV Length</span>
                <span className="text-foreground">{length} ft</span>
              </label>
              <input
                id="map-rv-length"
                type="range"
                min={20}
                max={120}
                value={length}
                onChange={(e) => setLength(Number(e.target.value))}
                className="mt-5 w-full accent-primary"
              />
            </div>

            <fieldset className="mt-8">
              <legend className="eyebrow text-muted-foreground">Site Preference</legend>
              <div className="mt-4 grid gap-2 sm:grid-cols-3">
                {preferences.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    aria-pressed={preference === opt}
                    onClick={() => setPreference(opt)}
                    className={cn(
                      "eyebrow border px-4 py-3.5 transition-colors",
                      preference === opt
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border text-muted-foreground hover:border-foreground hover:text-foreground",
                    )}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="mt-8">
              <legend className="eyebrow text-muted-foreground">Stay Type</legend>
              <div className="mt-4 grid gap-2 sm:grid-cols-3">
                {stayTypes.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    aria-pressed={stayType === opt}
                    onClick={() => setStayType(opt)}
                    className={cn(
                      "eyebrow border px-4 py-3.5 transition-colors",
                      stayType === opt
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border text-muted-foreground hover:border-foreground hover:text-foreground",
                    )}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </fieldset>

            <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
              This tool does not confirm that a specific site will fit your RV. Auburn RV Resort
              staff review length, configuration and availability when responding to your request.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <ResortButton type="submit" variant="solid" className="w-full sm:w-auto">
                Check My Options
              </ResortButton>
              <ResortLink variant="outline" href="/reservations/" className="w-full sm:w-auto">
                Go to Availability Request
              </ResortLink>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
